/**
 * Garde d'acces pour les modules (restaurant, wellness, giftvouchers, newsletter)
 *
 * Lecture synchrone depuis le session store. Pas de loader,
 * pas d'appel API : les donnees sont garanties READY par SessionProvider.
 *
 * @module ModuleAccessGuard
 * @author Equipe Brydge
 * @since 3.0.0
 */

import PropTypes from 'prop-types';
import {Navigate} from 'react-router-dom';
import {usePermissions} from 'packages/common/hooks/session';
import {ViewFeatureError} from 'apps/dashboard/pages/error/view';

const MODULE_NAMES = {
  restaurant: 'Restaurant',
  wellness: 'Wellness',
  giftvouchers: 'Bons Cadeaux',
};

/**
 * Modules pilotés par le toggle d'activation de l'établissement (et non par
 * l'abonnement) : l'écran d'erreur propose la réactivation dans les paramètres
 * plutôt que la souscription d'un abonnement.
 */
const SETTINGS_TOGGLED_MODULES = ['giftvouchers'];

const ModuleAccessGuard = ({moduleKey, children, redirectTo}) => {
  const {hasModuleAccess} = usePermissions();

  if (hasModuleAccess(moduleKey)) {
    return children;
  }

  // REDIRIGER PLUTÔT QU'EXPLIQUER, quand l'appelant le demande. L'écran
  // d'erreur annonce un module à souscrire ou à réactiver : pour une
  // fonctionnalité en déploiement progressif, il révélerait son existence à un
  // établissement à qui on ne l'a pas encore ouverte, et l'enverrait chercher
  // un réglage qui n'existe pas chez lui.
  if (redirectTo) {
    return <Navigate to={redirectTo} replace />;
  }

  return (
    <ViewFeatureError
      feature={moduleKey}
      moduleDetails={{name: MODULE_NAMES[moduleKey] || moduleKey}}
      reason={SETTINGS_TOGGLED_MODULES.includes(moduleKey) ? 'deactivated' : 'subscription'}
    />
  );
};

ModuleAccessGuard.propTypes = {
  moduleKey: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  /** Chemin de repli : rend une redirection au lieu de l'écran d'erreur. */
  redirectTo: PropTypes.string,
};

export default ModuleAccessGuard;
